import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { MapMove, MapPing } from '@features/mapVisualization/types';
import { classifyMove } from '@features/mapVisualization/constants';
import { computeProgress, computeEta } from '@features/mapVisualization/utils/routeProgress';
import {
  formatScenarioKey,
  formatSpeed,
  formatHeading,
  formatAccuracy,
  getStatusLabel,
  getStatusColor,
} from '@features/mapVisualization/utils/formatters';
import BatteryIndicator from '@features/mapVisualization/components/BatteryIndicator';

import type { MapDriver } from './useDriverMapData';
import PhotoLightbox from '@components/PhotoLightbox/PhotoLightbox';
import type { LightboxPhoto } from '@components/PhotoLightbox/PhotoLightbox';
import { sortImages, getHumanReadableLabel } from '@services/vehicleImagesService';
import { GoogleMapsElement, useMapSurfaceColors } from '@components/Maps';

import { getThumbnailUrl, gpuThumbnailCss } from '@utils/thumbnailUrl';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography, LinearProgress } from '@mui/material';
import {
  BiTachometer,
  BiNavigation,
  BiTargetLock,
} from 'react-icons/bi';

dayjs.extend(relativeTime);

// HELPERS ------------------------------------------------------------------- //

const PHOTO_FALLBACK_ORDER = ['driver-front', 'passenger-front', 'driver-side-full-view', 'passenger-side-full-view'];

const getVehicleImageUrl = (move: MapMove): string => {
  for (const stepId of PHOTO_FALLBACK_ORDER) {
    const deliveryPhoto = move.delivery_photos?.find(p => p.step_id === stepId)?.url;
    if (deliveryPhoto) return deliveryPhoto;
    const pickupPhoto = move.pickup_photos?.find(p => p.step_id === stepId)?.url;
    if (pickupPhoto) return pickupPhoto;
  }
  return '';
};

const buildLightboxPhotos = (move: MapMove): LightboxPhoto[] => {
  const photos: LightboxPhoto[] = [];
  const deliverySorted = sortImages([...(move.delivery_photos || [])]);
  const pickupSorted = sortImages([...(move.pickup_photos || [])]);

  for (const photo of deliverySorted) {
    if (photo.url && photo.step_id) {
      photos.push({ url: photo.url, label: `Delivery - ${getHumanReadableLabel(photo.step_id)}` });
    }
  }
  for (const photo of pickupSorted) {
    if (photo.url && photo.step_id) {
      photos.push({ url: photo.url, label: `Pickup - ${getHumanReadableLabel(photo.step_id)}` });
    }
  }
  return photos;
};

// TYPES --------------------------------------------------------------------- //

interface SelectedDriverOverlayProps {
  selectedDriver: MapDriver | null;
  driverMoves: MapMove[];
  pingsByMoveId: Record<number, MapPing[]>;
  unassignedPings: MapPing[];
  highlightedMoveId: number | null;
  moveColorMap: Record<number, string>;
}

// COMPONENT ----------------------------------------------------------------- //

const SelectedDriverOverlay = ({
  selectedDriver,
  driverMoves,
  pingsByMoveId,
  unassignedPings,
  highlightedMoveId,
  moveColorMap,
}: SelectedDriverOverlayProps) => {
  const theme = useTheme();
  const surface = useMapSurfaceColors();
  const cls = useStyles(theme, surface);

  // All pings for this driver (move-associated + unassigned), sorted by time
  const allPings = React.useMemo(() => {
    const pings: MapPing[] = [];
    driverMoves.forEach(m => {
      const movePings = pingsByMoveId[m.id] || [];
      pings.push(...movePings);
    });
    pings.push(...unassignedPings);
    pings.sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
    return pings;
  }, [driverMoves, pingsByMoveId, unassignedPings]);

  const latestPing = allPings.length > 0 ? allPings[allPings.length - 1] : null;
  const lastPingTime = latestPing?.time || selectedDriver?.gpslocations?.[0]?.time;

  // Classify moves
  const counts = { past: 0, active: 0, future: 0 };
  driverMoves.forEach(m => { counts[classifyMove(m)]++; });

  const highlightedMove = highlightedMoveId
    ? driverMoves.find(m => m.id === highlightedMoveId) ?? null
    : null;

  const isOnline = selectedDriver?.status === 'online';

  // Vehicle photo
  const vehicleImageUrl = highlightedMove ? getVehicleImageUrl(highlightedMove) : '';

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const lightboxPhotos = React.useMemo(
    () => highlightedMove ? buildLightboxPhotos(highlightedMove) : [],
    [highlightedMove]
  );

  // Progress & ETA
  const highlightedMovePings = highlightedMove ? (pingsByMoveId[highlightedMove.id] || []) : [];
  const progress = React.useMemo(
    () => highlightedMove ? computeProgress(highlightedMove, highlightedMovePings) : null,
    [highlightedMove, highlightedMovePings]
  );

  const movePingSpeed = React.useMemo(() => {
    if (highlightedMovePings.length === 0) return undefined;
    return highlightedMovePings[highlightedMovePings.length - 1].speed;
  }, [highlightedMovePings]);

  const moveColor = highlightedMove ? (moveColorMap[highlightedMove.id] || surface.primary) : surface.primary;
  const isComplete = highlightedMove?.status === 'delivery successful';
  const showProgress = progress && !isComplete;

  return (
    <>
      <GoogleMapsElement
        className={cls.overlay}
        style={{ top: 54, left: 10, width: 280, pointerEvents: 'none' }}
        disablePadding
      >
        {vehicleImageUrl ? (
          <img
            src={getThumbnailUrl(vehicleImageUrl, 56)}
            alt='Vehicle'
            className={cls.vehiclePhoto}
            onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
          />
        ) : null}

        {/* Driver Header */}
        {selectedDriver ? (
          <div className={cls.section}>
            <div className={cls.driverHeader}>
              {selectedDriver.user?.avatar_url ? (
                <img
                  src={getThumbnailUrl(selectedDriver.user.avatar_url, 32)}
                  alt={selectedDriver.user?.display_name || 'Driver'}
                  className={cls.driverAvatar}
                />
              ) : (
                <div className={cls.driverAvatarFallback}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: theme.palette.primary.contrastText, lineHeight: 1 }}>
                    {(selectedDriver.user?.display_name || '')
                      .split(' ')
                      .map(w => w[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase() || '?'}
                  </Typography>
                </div>
              )}
              <div>
                <div className={cls.row}>
                  <Typography variant='body2' sx={{ fontSize: 13, fontWeight: 700, color: surface.primary }}>
                    {selectedDriver.user?.display_name || `Driver #${selectedDriver.id}`}
                  </Typography>
                </div>
                <div className={cls.row}>
                  <span className={cls.statusDot} style={{ backgroundColor: isOnline ? '#22c55e' : '#6b7280' }} />
                  <Typography variant='body2' sx={{ fontSize: 11, color: surface.secondary, fontWeight: 500 }}>
                    {isOnline ? 'Online' : 'Offline'}
                  </Typography>
                  {lastPingTime ? (
                    <Typography variant='body2' sx={{ fontSize: 11, color: surface.disabled, ml: 0.75 }}>
                      {dayjs(lastPingTime).fromNow()}
                    </Typography>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Telemetry */}
        {latestPing ? (
          <div className={cls.section}>
            <div className={cls.divider} />
            <div className={cls.row}>
              <Typography variant='body2' sx={{ fontSize: 11, color: surface.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Latest Ping
              </Typography>
            </div>
            <div className={cls.statsGrid}>
              <div className={cls.stat}>
                <BiTachometer size={13} className={cls.statIcon} />
                <Typography variant='body2' sx={{ fontSize: 12, color: surface.primary, fontWeight: 600 }}>
                  {formatSpeed(latestPing.speed)}
                </Typography>
              </div>
              <div className={cls.stat}>
                <BiNavigation size={13} className={cls.statIcon} />
                <Typography variant='body2' sx={{ fontSize: 12, color: surface.primary, fontWeight: 600 }}>
                  {formatHeading(latestPing.heading)}
                </Typography>
              </div>
              <div className={cls.stat}>
                <BatteryIndicator level={latestPing.battery_level} charging={latestPing.battery_is_charging} />
              </div>
              <div className={cls.stat}>
                <BiTargetLock size={13} className={cls.statIcon} />
                <Typography variant='body2' sx={{ fontSize: 12, color: surface.primary, fontWeight: 600 }}>
                  {formatAccuracy(latestPing.accuracy)}
                </Typography>
              </div>
            </div>
          </div>
        ) : null}

        {/* Move Detail / Summary */}
        {driverMoves.length > 0 ? (
          <div className={cls.moveSection}>
            <div className={cls.divider} />

            {highlightedMove ? (
              <>
                {highlightedMovePings.length > 0 ? (
                  <div className={cls.pingCount}>
                    <BiTargetLock size={9} />
                    <Typography variant='body2' sx={{ fontSize: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'inherit' }}>
                      {highlightedMovePings.length} pings
                    </Typography>
                  </div>
                ) : null}
                <div className={cls.row}>
                  <span className={cls.moveDot} style={{ backgroundColor: moveColorMap[highlightedMove.id] || surface.primary }} />
                  <Typography variant='body2' sx={{ fontSize: 11, color: surface.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {getStatusLabel(classifyMove(highlightedMove))} Move
                  </Typography>
                </div>

                <div className={cls.row}>
                  <Typography variant='body2' sx={{ fontSize: 13, fontWeight: 700, color: surface.primary }}>
                    #{highlightedMove.id}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ fontSize: 11, fontWeight: 600, color: getStatusColor(highlightedMove.status, theme), ml: 1 }}
                  >
                    {getStatusLabel(highlightedMove.status)}
                  </Typography>
                </div>

                {highlightedMove.scenariotomoves?.[0]?.scenario ? (
                  <div className={cls.row}>
                    <Typography variant='body2' sx={{ fontSize: 12, color: surface.secondary }}>
                      {formatScenarioKey(highlightedMove.scenariotomoves[0].scenario.scenario_key)}
                    </Typography>
                  </div>
                ) : null}

                {highlightedMove.lane?.pickup?.name || highlightedMove.lane?.delivery?.name ? (
                  <div className={cls.row}>
                    <Typography variant='body2' sx={{ fontSize: 11, color: surface.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {highlightedMove.lane?.pickup?.name || '?'} &rarr; {highlightedMove.lane?.delivery?.name || '?'}
                    </Typography>
                  </div>
                ) : null}

                {showProgress ? (
                  <>
                    <div className={cls.progressRow}>
                      <LinearProgress
                        variant='determinate'
                        value={progress.percentage}
                        sx={{
                          flex: 1,
                          height: 3,
                          borderRadius: 2,
                          backgroundColor: surface.divider,
                          '& .MuiLinearProgress-bar': { backgroundColor: moveColor, borderRadius: 2 },
                        }}
                      />
                      <Typography variant='body2' sx={{ fontSize: 10, color: surface.secondary, fontWeight: 600, ml: 0.75, whiteSpace: 'nowrap' }}>
                        {Math.round(progress.percentage)}%
                      </Typography>
                    </div>
                    <div className={cls.row}>
                      <Typography variant='body2' sx={{ fontSize: 10, color: surface.disabled }}>
                        {computeEta(progress.remainingMeters, movePingSpeed)}
                      </Typography>
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <div className={cls.row}>
                  <Typography variant='body2' sx={{ fontSize: 11, color: surface.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Moves
                  </Typography>
                </div>
                <div className={cls.row} style={{ gap: 8 }}>
                  {counts.past > 0 ? (
                    <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 600, color: '#60a5fa' }}>
                      {counts.past} past
                    </Typography>
                  ) : null}
                  {counts.active > 0 ? (
                    <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 600, color: '#22c55e' }}>
                      {counts.active} driving
                    </Typography>
                  ) : null}
                  {counts.future > 0 ? (
                    <Typography variant='body2' sx={{ fontSize: 12, fontWeight: 600, color: '#fbbf24' }}>
                      {counts.future} upcoming
                    </Typography>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : null}
      </GoogleMapsElement>

      <PhotoLightbox
        open={lightboxOpen && lightboxPhotos.length > 0}
        photos={lightboxPhotos}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = (theme: Theme, surface: ReturnType<typeof useMapSurfaceColors>) => ({
  overlay: css`
    padding: 10px 14px;
    overflow: visible;
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
  `,
  moveSection: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    position: relative;
  `,
  pingCount: css`
    position: absolute;
    top: 10px;
    right: 0;
    display: flex;
    align-items: center;
    gap: 3px;
    color: ${surface.disabled};
  `,
  row: css`
    display: flex;
    align-items: center;
  `,
  divider: css`
    height: 1px;
    background-color: ${surface.divider};
    margin: 6px 0;
  `,
  statsGrid: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3px 12px;
  `,
  stat: css`
    display: flex;
    align-items: center;
    gap: 6px;
  `,
  statIcon: css`
    color: ${surface.disabled};
    flex-shrink: 0;
  `,
  driverHeader: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  driverAvatar: css`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    border: 1.5px solid ${surface.divider};
    ${gpuThumbnailCss}
  `,
  driverAvatarFallback: css`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${theme.palette.primary.main};
    border: 1.5px solid ${surface.divider};
  `,
  statusDot: css`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 5px;
    flex-shrink: 0;
  `,
  moveDot: css`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
  `,
  vehiclePhoto: css`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 6px;
    border: 2px solid ${surface.divider};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    z-index: 1;
    pointer-events: auto;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    ${gpuThumbnailCss}
    &:hover {
      transform: scale(1.08);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
    }
  `,
  progressRow: css`
    display: flex;
    align-items: center;
    margin-top: 4px;
  `,
});

export default SelectedDriverOverlay;
